import { GeoCoords } from '@App/app/entities/shared/geo-coords';
import { Injectable } from '@angular/core';
import { getCenter, getDistance } from 'geolib';
import { median } from '@App/app/shared/utils/utils';
import { FileMetadataInfo } from '../../../models/uploaded-image-metadata.model';
import { ValidationResult, ValidationWarning } from '../../../models/validation-warning.model';
import {
  validateCaptureTimeline,
  validateExifMetadata,
  validateSetConsistency,
  validateSpatialDistribution,
} from './photo-set-validators';

@Injectable({ providedIn: 'root' })
export class PhotosValidationService {
  validatePhotos(photos: FileMetadataInfo[]): ValidationResult {
    const { filesToValidate, noCoords } = this.partitionByCoords(photos);
    const center = getCenter(filesToValidate.map((f) => f.coords as GeoCoords));
    const wrongCoords = center ? this.findOutliers(filesToValidate, center) : [];

    const warnings: ValidationWarning[] = [
      ...validateExifMetadata(photos),
      ...validateSetConsistency(photos),
      ...validateSpatialDistribution(filesToValidate, center || undefined),
      ...validateCaptureTimeline(photos),
    ];

    return { noCoords, wrongCoords, warnings };
  }

  private partitionByCoords(photos: FileMetadataInfo[]) {
    const filesToValidate: FileMetadataInfo[] = [];
    const noCoords: string[] = [];
    for (const photo of photos) {
      if (photo.coords && photo.altitude) {
        filesToValidate.push(photo);
      } else {
        noCoords.push(photo.name);
      }
    }
    return { filesToValidate, noCoords };
  }

  private findOutliers(files: FileMetadataInfo[], center: GeoCoords): string[] {
    const altitudes = files.map((f) => f.altitude as number);
    const altitudeMedian = median(altitudes);
    const meanDistance = this.getMeanDistanceToCenter(files, center);
    return this.findTooDistancedCoords(files, center, meanDistance, altitudeMedian);
  }

  private findTooDistancedCoords(
    files: FileMetadataInfo[],
    center: GeoCoords,
    meanDistance: number,
    altitudeMedian: number,
    threshold = 100,
  ): string[] {
    return files
      .filter((file) => {
        return (
          file.coords &&
          file.altitude &&
          (getDistance(file.coords, center) > meanDistance + threshold ||
            file.altitude < altitudeMedian - threshold ||
            file.altitude > altitudeMedian + threshold)
        );
      })
      .map((file) => file.name);
  }

  private getMeanDistanceToCenter(files: FileMetadataInfo[], center: GeoCoords): number {
    return (
      files.reduce((sum, file) => sum + (file.coords ? getDistance(file.coords, center) : 0), 0) /
      files.length
    );
  }
}
